
import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { CampaignForm, CampaignFormValues } from "@/components/forms/CampaignForm";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Campaign } from "@/hooks/useCampaigns";

export default function EditCampaign() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [initialValues, setInitialValues] = useState<Partial<CampaignFormValues> | null>(null);
  
  // Use React Query to fetch campaign data
  const { data: campaign, isLoading, error } = useQuery({
    queryKey: ['campaign', id],
    queryFn: async () => {
      if (!id) throw new Error("Campaign ID is undefined");
      
      // Fetch campaign data
      const { data: campaignData, error: campaignError } = await supabase
        .from('campaigns')
        .select('*')
        .eq('id', id)
        .single();
      
      if (campaignError) throw campaignError;
      
      // Fetch venues for this campaign
      const { data: venuesData, error: venuesError } = await supabase
        .from('campaign_venues')
        .select('venue_id')
        .eq('campaign_id', id);
      
      if (venuesError) throw venuesError;
      
      return {
        ...campaignData,
        venues: venuesData.map(v => v.venue_id)
      } as Campaign & { venues: string[] };
    },
    enabled: !!id
  });
  
  // Show error toast if fetch fails
  useEffect(() => {
    if (error) {
      toast.error("Failed to load campaign data");
      console.error("Error loading campaign:", error);
    }
  }, [error]);
  
  // Transform the campaign data to match the form structure
  useEffect(() => {
    if (campaign) {
      setInitialValues({
        title: campaign.title,
        description: campaign.description,
        campaignType: campaign.campaign_type,
        voucherCount: campaign.vouchers_total,
        startDate: new Date(campaign.start_date),
        endDate: new Date(campaign.end_date),
        voucherImage: campaign.voucher_image || undefined,
        voucherDetails: campaign.voucher_details,
        sponsorName: campaign.sponsor_name,
        sponsorMessage: campaign.sponsor_message || undefined,
        isDraft: campaign.status === "draft",
        venues: campaign.venues || []
      });
    }
  }, [campaign]);
  
  const handleSubmit = async (values: CampaignFormValues) => {
    try {
      if (!id) throw new Error("Campaign ID is undefined");
      
      // Determine status based on isDraft and dates
      const now = new Date();
      let status: 'draft' | 'active' | 'scheduled' | 'ended' = 'draft';
      
      if (!values.isDraft) {
        const startDate = new Date(values.startDate);
        const endDate = new Date(values.endDate);
        
        if (endDate < now) {
          status = 'ended';
        } else if (startDate <= now) {
          status = 'active';
        } else {
          status = 'scheduled';
        }
      }
      
      // Update campaign in Supabase
      const { error: updateError } = await supabase
        .from('campaigns')
        .update({
          title: values.title,
          description: values.description,
          campaign_type: values.campaignType,
          vouchers_total: values.voucherCount,
          start_date: values.startDate.toISOString(),
          end_date: values.endDate.toISOString(),
          voucher_image: values.voucherImage || null,
          voucher_details: values.voucherDetails,
          sponsor_name: values.sponsorName,
          sponsor_message: values.sponsorMessage || null,
          status: status,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);
      
      if (updateError) throw updateError;
      
      // Update venue connections - first delete existing ones
      const { error: deleteVenuesError } = await supabase
        .from('campaign_venues')
        .delete()
        .eq('campaign_id', id);
      
      if (deleteVenuesError) throw deleteVenuesError;
      
      // Add new venue connections
      if (values.venues.length > 0) {
        const campaignVenues = values.venues.map(venueId => ({
          campaign_id: id,
          venue_id: venueId
        }));
        
        const { error: insertVenuesError } = await supabase
          .from('campaign_venues')
          .insert(campaignVenues);
        
        if (insertVenuesError) throw insertVenuesError;
      }
      
      toast.success("Campaign updated successfully!");
      navigate("/admin/campaigns");
    } catch (error) {
      console.error("Error updating campaign:", error);
      toast.error("Failed to update campaign");
    }
  };
  
  if (isLoading) {
    return (
      <AdminLayout title="Loading..." subtitle="Please wait">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }
  
  if (error || !initialValues) {
    return (
      <AdminLayout title="Error" subtitle="Campaign not found">
        <div className="bg-destructive/10 text-destructive p-4 rounded-md">
          <p>The campaign you're trying to edit wasn't found. It may have been deleted.</p>
          <button 
            onClick={() => navigate("/admin/campaigns")}
            className="mt-4 text-primary underline"
          >
            Return to campaigns list
          </button>
        </div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout 
      title="Edit Campaign" 
      subtitle={`Editing: ${initialValues.title}`}
    >
      <CampaignForm 
        onSubmit={handleSubmit} 
        initialValues={initialValues}
        isEditMode
      />
    </AdminLayout>
  );
}
